/**
 * The hub weather card. Prop-free by design — the integrator drops <WeatherCard /> into the
 * home screen and it owns its own read, its own states, its own retry.
 *
 * Every state tells the truth. A failed read is never rendered as a forecast, an unset
 * location is an honest "set it up" (not a fake 0°), and today is labelled as today.
 */
import { useId } from 'react';
import { Link } from 'react-router-dom';
import { useIsAdult } from '../../lib/identity';
import type { Weather } from '../../shared/types';
import { Button, Note, Skeleton, WeatherGlyph, useAsync } from './ui';
import { explain, getWeather, isToday, temp, weekdayShort } from './weatherApi';

export default function WeatherCard() {
  const adult = useIsAdult();
  const headingId = useId();
  const weather = useAsync<Weather>(getWeather);

  return (
    <section
      aria-labelledby={headingId}
      className="overflow-hidden rounded-card border border-border bg-gradient-to-br from-dusk-soft/60 via-surface to-surface shadow-[0_1px_3px_rgba(43,39,35,0.05)]"
    >
      <div className="p-6 sm:p-7">
        <div className="flex items-center justify-between gap-3">
          <h2
            id={headingId}
            className="flex items-center gap-2 text-xs font-semibold tracking-[0.12em] text-ink-soft uppercase"
          >
            <PinIcon className="h-4 w-4 text-dusk" />
            Weather
          </h2>
          {weather.data?.configured && weather.data.place && (
            <span className="truncate text-sm text-ink-soft">{weather.data.place}</span>
          )}
        </div>

        <div className="mt-5">
          {weather.loading && (
            <div className="space-y-4" role="status" aria-live="polite">
              <span className="sr-only">Loading the forecast…</span>
              <div className="flex items-center gap-4">
                <Skeleton className="h-14 w-24" />
                <Skeleton className="h-10 w-10 rounded-full" />
              </div>
              <Skeleton className="h-4 w-40" />
              <div className="flex gap-2">
                {[0, 1, 2, 3, 4].map((i) => (
                  <Skeleton key={i} className="h-20 w-14" />
                ))}
              </div>
            </div>
          )}

          {!weather.loading && weather.error !== null && (
            <div className="space-y-3">
              <Note tone="warn" role="alert">
                {explain(weather.error, 'Couldn’t load the weather just now.')}
              </Note>
              <Button onClick={weather.reload}>Try again</Button>
            </div>
          )}

          {!weather.loading && weather.error === null && weather.data && (
            <Body data={weather.data} adult={adult} />
          )}
        </div>
      </div>
    </section>
  );
}

function Body({ data, adult }: { data: Weather; adult: boolean }) {
  // Nothing set up yet — an honest setup state, never a fabricated 0°.
  if (!data.configured) {
    return (
      <div className="space-y-4">
        <p className="text-sm leading-relaxed text-ink-soft">
          {adult
            ? 'Add your home location and the local forecast lands right here.'
            : 'A grown-up still needs to set your home location.'}
        </p>
        {adult && (
          <Link
            to="/admin"
            className="inline-flex min-h-11 items-center rounded-full bg-sage px-4 text-sm font-medium text-white transition-colors hover:bg-sage/90 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sage"
          >
            Set your location
          </Link>
        )}
      </div>
    );
  }

  const today = data.daily[0] ?? null;
  const upcoming = data.daily.slice(1, 7);

  return (
    <div>
      <div className="flex items-center gap-4">
        {data.current && (
          <WeatherGlyph code={data.current.code} className="h-12 w-12 shrink-0 text-dusk" />
        )}
        <div className="min-w-0">
          <p className="font-display text-5xl leading-none font-semibold text-ink">
            {data.current ? temp(data.current.temp_f) : '—'}
          </p>
          <p className="mt-1 truncate text-sm text-ink-soft">
            {data.current?.label ?? 'Forecast unavailable'}
            {today && (
              <>
                {' '}
                <span aria-hidden="true" className="text-ink-faint">
                  ·
                </span>{' '}
                <span className="whitespace-nowrap">
                  H {temp(today.high_f)} / L {temp(today.low_f)}
                </span>
              </>
            )}
          </p>
        </div>
      </div>

      {upcoming.length > 0 && (
        <ul className="mt-5 flex gap-2 overflow-x-auto border-t border-border pt-4">
          {upcoming.map((day) => (
            <li
              key={day.date}
              className="flex min-w-14 flex-1 flex-col items-center gap-1 rounded-xl px-1 py-1 text-center"
            >
              <span className="text-xs font-medium text-ink-soft">
                {isToday(day.date) ? 'Today' : weekdayShort(day.date)}
              </span>
              <WeatherGlyph code={day.code} className="h-6 w-6 text-ink-soft" />
              <span className="sr-only">{day.label}. </span>
              <span className="text-sm font-semibold text-ink">{temp(day.high_f)}</span>
              <span className="text-xs text-ink-faint">{temp(day.low_f)}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function PinIcon({ className = 'h-4 w-4' }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.7}
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      aria-hidden="true"
      focusable="false"
    >
      <path d="M12 21s7-5.5 7-11a7 7 0 1 0-14 0c0 5.5 7 11 7 11Z" />
      <circle cx="12" cy="10" r="2.5" />
    </svg>
  );
}
